import { useContext } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { UNSAFE_LocationContext } from 'react-router';

import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';
import type { CategoryGroupEntity } from '@actual-app/core/types/models';

import { BitacoraPage } from '#components/mobile/bitacora/BitacoraPage';
import { FichaCategoriaPage } from '#components/mobile/budget/ficha/FichaCategoriaPage';
import { HojaNuevaCategoria } from '#components/mobile/budget/HojaNuevaCategoria';
import { HojaZarparEjemplo } from '#components/mobile/budget/HojaZarpar';
import type { RellenoZarpar } from '#components/mobile/budget/HojaZarpar';
import { nuevaBase } from '#components/mobile/budget/recuerdos';
import type { Recuerdo } from '#components/mobile/budget/recuerdos';
import { LlegadaBotella } from '#components/mobile/budget/travesia/LlegadaBotella';
import { separarEmoji, unirEmoji } from '#components/mobile/ui/emoji';
import { color } from '#components/mobile/ui/tokens';
import { MobilePageHeaderProvider } from '#components/Page';

/**
 * Lo que la ayuda de Gavi abre de ejemplo sobre Ahorros: la hoja «Nueva
 * travesía» (con el selector de grupo o ya con grupo), la de zarpar (vacía o
 * escrita), la llegada de una botella, la bitácora o la ficha de una
 * travesía de verdad. Nada de esto guarda.
 */
export type EjemploAhorros =
  | { que: 'nueva'; eligiendo: boolean; conGrupo: boolean }
  | { que: 'zarpar'; lleno: boolean }
  | { que: 'llegada' }
  | { que: 'bitacora' }
  | { que: 'ficha'; categoriaId: string };

/** Por debajo de las hojas (205) y de la ayuda; por encima de las pestañas (100). */
const Z_VISTA_PREVIA = 150;

const IMPORTE_EJEMPLO = 600_00;
const MESES_EJEMPLO = 8;

export function EjemploAyuda({
  ejemplo,
  grupos,
  onCerrar,
}: {
  ejemplo: EjemploAhorros;
  /** Grupos de gasto a la vista: el de ejemplo es el de ahorro si lo hay. */
  grupos: CategoryGroupEntity[];
  onCerrar: () => void;
}) {
  const { t } = useTranslation();
  const mes = monthUtils.currentMonth();
  const hoy = monthUtils.currentDay();
  const nombre = unirEmoji('🧳', t('Trip to Lisbon'));
  const relleno: RellenoZarpar = {
    importe: IMPORTE_EJEMPLO,
    fecha: `${monthUtils.addMonths(mes, MESES_EJEMPLO)}-01`,
    clase: 'viaje',
    carta: t('Lisbon at last: custard tarts by the river, with no rush.'),
    siEntonces: t('When I get paid, I set aside €75'),
  };

  switch (ejemplo.que) {
    case 'nueva':
      return (
        <HojaNuevaCategoria
          key={`${ejemplo.eligiendo}-${ejemplo.conGrupo}`}
          modo="categoria"
          titulo={t('New voyage')}
          textoBoton={t('Continue')}
          grupos={grupos}
          onCrear={() => undefined}
          onCrearGrupo={async () => null}
          ejemplo={{
            nombre,
            grupoId: ejemplo.conGrupo
              ? (grupoDeAhorro(grupos)?.id ?? null)
              : null,
            eligiendo: ejemplo.eligiendo,
          }}
          onCerrar={onCerrar}
        />
      );
    case 'zarpar':
      return (
        <HojaZarparEjemplo
          nombre={nombre}
          month={mes}
          relleno={ejemplo.lleno ? relleno : null}
          onCerrar={onCerrar}
        />
      );
    case 'llegada':
      return (
        <LlegadaBotella
          recuerdo={recuerdoEjemplo(nombre, relleno, hoy)}
          disp="ayuda"
          soloLectura={false}
          conGesto={false}
          onGuardar={() => undefined}
          onResponder={async () => false}
          onVerBitacora={() => undefined}
          onCerrar={onCerrar}
        />
      );
    case 'bitacora':
      return (
        <VistaPrevia>
          <BitacoraPage vistaPrevia />
        </VistaPrevia>
      );
    case 'ficha':
      return (
        <VistaPrevia>
          <FichaCategoriaPage
            vistaPrevia={{ categoryId: ejemplo.categoriaId }}
          />
        </VistaPrevia>
      );
    default:
      return null;
  }
}

/** El grupo de ahorro (por su nombre) o, si no hay, el primero. */
function grupoDeAhorro(
  grupos: CategoryGroupEntity[],
): CategoryGroupEntity | null {
  return (
    grupos.find(g => /ahorr|estalvi|saving/i.test(g.name)) ?? grupos[0] ?? null
  );
}

/** La botella del ejemplo, recién llegada hoy (no está en ninguna parte). */
function recuerdoEjemplo(
  nombre: string,
  relleno: RellenoZarpar,
  hoy: string,
): Recuerdo {
  const { emoji, resto } = separarEmoji(nombre);
  const base = nuevaBase({
    categoriaId: 'ayuda-ejemplo',
    objetivo: { tipo: 'una-vez', importe: relleno.importe, fecha: hoy },
    nombre: resto,
    emoji,
    saldo: 0,
    hoy: monthUtils.subDays(hoy, MESES_EJEMPLO * 30),
    clase: relleno.clase,
    carta: relleno.carta,
    siEntonces: relleno.siEntonces,
  });
  return {
    base,
    aportes: {},
    tieneFoto: false,
    llegada: {
      fecha: hoy,
      saldo: relleno.importe,
      meta: relleno.importe,
      aportaciones: MESES_EJEMPLO,
      ajustada: false,
      diasRespectoPlazo: 0,
    },
    abierta: null,
    firmas: [],
    respuestas: [],
  };
}

/**
 * Otra pantalla pintada encima de Ahorros (la bitácora, una ficha): con su
 * cabecera dentro y sin recordar su desplazamiento como el de Ahorros.
 */
const ContextoLugar = UNSAFE_LocationContext;

function VistaPrevia({ children }: { children: ReactNode }) {
  const contexto = useContext(ContextoLugar);
  // Al `body`: la página de Ahorros se anima con `transform` y dejaría la
  // vista previa por debajo de la barra de pestañas.
  return createPortal(
    <ContextoLugar.Provider
      value={{
        ...contexto,
        location: { ...contexto.location, key: 'ayuda-vista-previa' },
      }}
    >
      <MobilePageHeaderProvider>
        <View
          data-testid="ayuda-vista-previa"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: Z_VISTA_PREVIA,
            backgroundColor: color.bg,
          }}
        >
          {children}
        </View>
      </MobilePageHeaderProvider>
    </ContextoLugar.Provider>,
    document.body,
  );
}
